'use client';

import { useState } from 'react';
import Link from 'next/link';

import { Button, Typography } from 'lighthouse';

import CatalogProductCard from '@/widgets/CatalogProductCard';
import { CatalogProductCardLoading } from '@/widgets/CatalogProductCard/CatalogProductCardLoading';
import { catalogItemFromOffer } from '@/widgets/CatalogProductCard/mappers';
import { Slider } from '@/widgets/Slider';

import { ShelfFooter } from './_components/ShelfFooter';
import { ShelfHeader } from './_components/ShelfHeader';
import { ShelfSeeAllCard } from './_components/ShelfSeeAllCard';
import { ShelfTabs } from './_components/ShelfTabs';
import { ProductShelfProps, ProductShelfTab } from './models';
import { useShelfProducts } from './useShelfProducts';

const SECTION_CLASS = {
    white: 'bg-white py-12 md:py-20',
    soft: 'bg-gradient-to-b from-white via-sky-50/50 to-white py-12 md:py-20',
    dark: 'relative isolate overflow-hidden bg-gradient-to-l from-slate-950 via-slate-900 to-sky-950 py-12 md:py-20'
};

const HIDE_MOBILE_ARROWS = 'max-md:[&_.custom-swiper-button-next]:hidden max-md:[&_.custom-swiper-button-prev]:hidden';

export const ProductShelf = ({ badge, title, subtitle, tabs, trustPoints, tone = 'white' }: ProductShelfProps) => {
    const [activeTab, setActiveTab] = useState<ProductShelfTab>(tabs[0]);
    const { sectionRef, resultKey, result, inView, retry } = useShelfProducts(activeTab);
    const isDark = tone === 'dark';
    const items = result?.items ?? [];
    const isLoading = !result;
    const hasError = !!result?.error;
    const isEmpty = !!result && !hasError && !items.length;

    return (
        <section ref={sectionRef} aria-label={title} className={SECTION_CLASS[tone]}>
            {isDark && (
                <>
                    <div aria-hidden className='pointer-events-none absolute -left-24 -top-32 -z-10 h-96 w-96 rounded-full bg-primary/20 blur-3xl' />
                    <div
                        aria-hidden
                        className='pointer-events-none absolute -bottom-32 right-1/3 -z-10 h-72 w-72 rounded-full bg-secondary/25 blur-3xl'
                    />
                </>
            )}
            <div className='desktop-width px-4'>
                <ShelfHeader
                    badge={badge}
                    title={title}
                    subtitle={subtitle}
                    href={activeTab.href}
                    linkLabel={isDark ? `مشاهده همهٔ ${activeTab.label}` : `همهٔ ${activeTab.label}`}
                    dark={isDark}
                />

                {tabs.length > 1 && (
                    <div className='mb-5 md:mb-6'>
                        <ShelfTabs label={title} tabs={tabs} activeId={activeTab.id} onSelect={setActiveTab} dark={isDark} />
                    </div>
                )}

                <div aria-busy={isLoading && inView} className='min-h-44 md:min-h-[22rem]'>
                    <span role='status' className='sr-only'>
                        {isLoading ? '' : hasError ? 'خطا در دریافت محصولات' : `${items.length.toLocaleString('fa-IR')} محصول نمایش داده شد`}
                    </span>
                    {isLoading ? (
                        <div
                            className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:[&>div:nth-child(2)]:block
                                [&>div:nth-child(n+2)]:hidden lg:[&>div:nth-child(n+2)]:block'>
                            <CatalogProductCardLoading count={4} isCompact />
                        </div>
                    ) : hasError || isEmpty ? (
                        <div
                            className='flex min-h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-N200 bg-white
                                px-5 py-10 text-center'>
                            <Typography variant='subtitle1' component='p' color='blackHighEmphasis' className='!m-0 font-bold'>
                                {hasError ? 'محصولات دریافت نشدن' : 'فعلاً محصولی برای شهرت پیدا نشد'}
                            </Typography>
                            <Typography variant='body2' component='p' color='blackMediumEmphasis' className='!m-0 leading-7'>
                                {hasError ? 'اتصال اینترنت رو بررسی کن و دوباره تلاش کن.' : 'می‌تونی همهٔ محصولات این دسته رو ببینی.'}
                            </Typography>
                            {hasError ? (
                                <Button variant='outlined' onClick={retry} className='min-h-11'>
                                    تلاش دوباره
                                </Button>
                            ) : (
                                <Button component={Link} href={activeTab.href} variant='outlined' className='min-h-11'>
                                    مشاهده همهٔ {activeTab.label}
                                </Button>
                            )}
                        </div>
                    ) : (
                        <Slider
                            key={resultKey}
                            dir='rtl'
                            spaceBetween={16}
                            slidesPerView={1.12}
                            navigation
                            watchOverflow
                            grabCursor
                            swiperSlideClassName='!h-auto'
                            breakpoints={{ 640: { slidesPerView: 2.15 }, 1024: { slidesPerView: 3.15 }, 1280: { slidesPerView: 4.15 } }}
                            className={`!relative !px-1 !py-2 ${HIDE_MOBILE_ARROWS}`}>
                            {[
                                ...items.map((offer) => (
                                    <CatalogProductCard
                                        key={offer.productUuid}
                                        item={catalogItemFromOffer(offer)}
                                        showSelectButton={false}
                                        isCompact
                                    />
                                )),
                                <ShelfSeeAllCard key='see-all' href={activeTab.href} label={activeTab.label} total={result.total} dark={isDark} />
                            ]}
                        </Slider>
                    )}
                </div>

                <ShelfFooter trustPoints={trustPoints} dark={isDark} />
            </div>
        </section>
    );
};
